import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { assets, features, SITE, localFallbackProducts } from '../data/mock';
import { SHOPIFY_ENABLED } from '../lib/shopify';
import ProductCard from '../components/ProductCard';
import HeroVideo from '../components/HeroVideo';
import AdminHero from '../components/AdminHero';
import SignatureHero from '../components/SignatureHero';
import CollectionShowcase from '../components/CollectionShowcase';
import Craftsmanship from '../components/Craftsmanship';
import LifestyleGallery from '../components/LifestyleGallery';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import InstagramGrid from '../components/InstagramGrid';
import PressMarquee from '../components/PressMarquee';
import FAQ from '../components/FAQ';
import useReveal from '../hooks/useReveal';
import useParallax from '../hooks/useParallax';
import { useProducts } from '../hooks/useShopify';
import { useStoreProducts } from '../hooks/useStoreProducts';
import { useCopy } from '../context/SiteCopyContext';
import { ArrowRight, Loader2, Truck, RotateCcw, ShieldCheck } from 'lucide-react';

const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`reveal ${visible ? 'in' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const { products: shopifyProducts, loading: productsLoading } = useProducts({ first: 8 });
  // Local-first: when Shopify is off / empty, show the bundled fallback products
  // so the home page still feels populated using 100% self-hosted media.
  const products = (!SHOPIFY_ENABLED || (!productsLoading && shopifyProducts.length === 0))
    ? localFallbackProducts
    : shopifyProducts;

  // "In the spotlight" — pulls live DB products flagged `featured=true`, in
  // sort_order. Admin controls the picks via the Featured toggle in /admin.
  const { products: dbProducts, loading: spotlightLoading } = useStoreProducts();
  const spotlightProducts = React.useMemo(() => {
    const featured = (dbProducts || []).filter((p) => p.featured);
    const list = featured.length > 0 ? featured : (dbProducts || []).slice(0, 3);
    return list.map((p) => {
      const primary = p.media?.primary_image;
      const gallery = p.media?.gallery_images || [];
      const seen = new Set();
      const images = [];
      for (const url of [primary, ...gallery]) {
        if (url && !seen.has(url)) { seen.add(url); images.push(url); }
      }
      return {
        ...p,
        images,
        preorder: p.status === 'preorder',
        soldOut: p.status === 'sold-out',
        availableForSale: p.status === 'available' || p.status === 'preorder',
        handle: p.id,
        slug: p.id,
      };
    });
  }, [dbProducts]);

  // Site copy with safe fallbacks
  const spotlightEyebrow = useCopy('home.spotlight.eyebrow', 'Featured');
  const spotlightTitle = useCopy('home.spotlight.title', 'In the spotlight.');
  const spotlightViewAll = useCopy('home.spotlight.view_all', 'View all');
  const promiseEyebrow = useCopy('home.promise.eyebrow', 'Our promise');
  const promiseTitle = useCopy('home.promise.title', 'What you get with every order.');
  const promiseF1Title = useCopy('home.promise.feature_1_title', 'Free Australia-wide delivery');
  const promiseF1Desc = useCopy('home.promise.feature_1_desc', 'On every order. No hidden costs.');
  const promiseF2Title = useCopy('home.promise.feature_2_title', '30-day returns');
  const promiseF2Desc = useCopy('home.promise.feature_2_desc', 'Any problems, any reason — we take it back within 30 days. No fees, no fuss.');
  const promiseF3Title = useCopy('home.promise.feature_3_title', 'Built to outlive the trend');
  const promiseF3Desc = useCopy('home.promise.feature_3_desc', '5 years of warranty on every frame. We are not worried — you should not be either.');

  return (
    <main className="bg-[#F5EDDD]">
      <SEO
        title="Solid timber & bouclé furniture, made in Australia"
        description="Premium bouclé bed frames, modular sofas and solid timber furniture. Designed and made in Australia. Free Australia-wide delivery, 30-day returns, 5-year warranty."
        path="/"
      />
      {/* 1. HERO VIDEO */}
      <AdminHero />

      {/* 2. PRESS MARQUEE — "As seen in" */}
      <PressMarquee />

      {/* 3. SIGNATURE BRAND IMAGE — timber + bouclé split */}
      <SignatureHero />

      {/* 3b. BRAND PARAGRAPH (no eyebrow, no headline — flows from SignatureHero) */}
      <section className="bg-[#F5EDDD] grain">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-2 hidden lg:block" />
          <Reveal delay={120} className="lg:col-span-8 lg:col-start-3">
            <div className="space-y-5 text-[#5B3A24] text-[16px] md:text-[17px] leading-[1.95] text-center">
              <p>
                Timber &amp; Thread was built around a single belief — that the room you spend a third of your life
                in deserves real materials, not flat-pack stand-ins.
              </p>
              <p>
                <span className="text-[#33200F]">Timber</span> is solid hardwood, hand-joined and built to outlast trends.
                {' '}<span className="text-[#33200F]">Thread</span> is heavyweight bouclé, hand-upholstered to soften the room
                the moment you walk in.
              </p>
              <p className="text-[#33200F]">
                Two collections, one workshop, all made in Australia.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-[#33200F] link-underline pt-3"
              >
                Read our story <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. COLLECTION SHOWCASES (Thread + Timber, distinct treatments) */}
      <CollectionShowcase />

      {/* 5. FEATURED PRODUCTS (live from Shopify) */}
      <section className="bg-[#EDE3CF] py-16 md:py-20 grain">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-[11px] tracking-[0.32em] uppercase text-[#A07A4F] mb-3">— {spotlightEyebrow}</p>
                <h2
                  className="text-3xl md:text-4xl text-[#33200F]"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {spotlightTitle}
                </h2>
              </div>
              <Link
                to="/collections"
                className="hidden sm:inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-[#33200F] link-underline"
              >
                {spotlightViewAll} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {spotlightLoading ? (
              <div className="lg:col-span-3 py-16 flex justify-center">
                <Loader2 className="h-6 w-6 text-[#A07A4F] animate-spin" strokeWidth={1.4} />
              </div>
            ) : spotlightProducts.length === 0 ? (
              <div className="lg:col-span-3 py-16 text-center text-[#A07A4F] italic">No featured products yet. Toggle “Featured on Homepage” in /admin to add some.</div>
            ) : (
              spotlightProducts.slice(0, 3).map((p, i) => (
                <Reveal key={p.id} delay={i * 80}>
                  <ProductCard product={p} />
                </Reveal>
              ))
            )}
          </div>
        </div>
      </section>

      {/* 6. CRAFTSMANSHIP & MATERIALS */}
      <Craftsmanship />

      {/* 7. TESTIMONIALS */}
      <TestimonialsCarousel />

      {/* 8. LIFESTYLE GALLERY */}
      <LifestyleGallery />

      {/* 9. PROMISE / FEATURES (icons not photos) */}
      <section className="bg-[#EDE3CF] py-24 grain">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-[11px] tracking-[0.32em] uppercase text-[#A07A4F] mb-3">— {promiseEyebrow}</p>
              <h2
                className="text-3xl md:text-5xl text-[#33200F]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {promiseTitle}
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14 max-w-5xl mx-auto">
            {[
              { Icon: Truck, title: promiseF1Title, desc: promiseF1Desc },
              { Icon: RotateCcw, title: promiseF2Title, desc: promiseF2Desc },
              { Icon: ShieldCheck, title: promiseF3Title, desc: promiseF3Desc },
            ].map((f, idx) => {
              const Icon = f.Icon;
              return (
                <Reveal key={idx} delay={idx * 140} className="text-center">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#C9B596] text-[#5B3A24] mb-6">
                    <Icon className="h-7 w-7" strokeWidth={1.3} />
                  </div>
                  <h3
                    className="text-2xl text-[#33200F] mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {f.title}
                  </h3>
                  <p className="text-[#5B3A24] text-[14px] leading-relaxed max-w-xs mx-auto">{f.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 10. INSTAGRAM */}
      <InstagramGrid />

      {/* 12. FAQ */}
      <FAQ />

      {/* 13. CLOSING CTA — parallax background */}
      <ClosingCTA />
    </main>
  );
};

// Closing CTA extracted to use parallax cleanly
const ClosingCTA = () => {
  const [ref, y] = useParallax(50);
  const [innerRef, innerVisible] = useReveal();
  return (
    <section
      ref={ref}
      className="relative py-32 md:py-40 overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{ backgroundImage: `url(${assets.branded})`, transform: `translate3d(0, ${y}px, 0) scale(1.08)` }}
      />
      <div className="absolute inset-0 bg-[#1E140B]/65" />
      <div
        ref={innerRef}
        className={`reveal ${innerVisible ? 'in' : ''} relative max-w-3xl mx-auto px-6 text-center text-[#F5EDDD]`}
      >
        <p className="text-[11px] tracking-[0.32em] uppercase text-[#E8DBC4] mb-5">— Worth waking up to</p>
        <h2
          className="text-4xl md:text-6xl leading-[1.05] mb-8"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Make the room you spend a third of your life in,
          <span className="italic text-[#E8DBC4]"> count.</span>
        </h2>
        <Button
          asChild
          className="bg-[#F5EDDD] text-[#33200F] hover:bg-white rounded-none h-12 px-10 text-[12px] tracking-[0.22em] uppercase"
        >
          <Link to="/collections">Shop the collection</Link>
        </Button>
        <p className="mt-8 text-[12px] tracking-[0.22em] uppercase text-[#E8DBC4]/80">
          Questions? <a href={`mailto:${SITE.email}`} className="underline underline-offset-4 hover:text-[#F5EDDD]">{SITE.email}</a>
        </p>
      </div>
    </section>
  );
};

export default Home;
